import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import {
  generateIncentiveStatementPDF,
  downloadIncentiveStatementPDF,
  IncentiveStatementData,
} from '../../utils/incentive-statement-pdf';

// APIレスポンスの型
interface IncentiveDetail {
  constructionNumber: string;
  companyName: string;
  title: string;
  amount: number;
  role: 'assigned' | 'pm' | 'promoter';
}

interface UserIncentive {
  userId: string;
  userName: string;
  departmentName: string;
  details: IncentiveDetail[];
  totalAmount: number;
}

interface StatementsResponse {
  statements: UserIncentive[];
  grandTotal: number;
  period: { startDate: string; endDate: string };
}

interface UserOption {
  id: string;
  name: string;
  departmentName?: string;
}

// 月の最初と最後の日を取得
function getMonthRange(year: number, month: number): { start: string; end: string } {
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start, end };
}

// 今日の日付をYYYY-MM-DD形式で取得
function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function IncentiveStatementList() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // 期間選択
  const [periodMode, setPeriodMode] = useState<'month' | 'custom'>('month');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // 支給日
  const [paymentDate, setPaymentDate] = useState(getTodayStr());

  // ユーザー選択
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // データ
  const [statements, setStatements] = useState<UserIncentive[]>([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ユーザー一覧取得
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await api.get<{ items: UserOption[] }>('/api/users?pageSize=500&status=active');
        setAllUsers(data.items || []);
      } catch (error) {
        console.error('ユーザー取得エラー:', error);
      }
    };
    fetchUsers();
  }, []);

  // 期間の日付を計算
  const getDateRange = useCallback((): { startDate: string; endDate: string } | null => {
    if (periodMode === 'month') {
      const range = getMonthRange(selectedYear, selectedMonth);
      return { startDate: range.start, endDate: range.end };
    } else {
      if (!customStartDate || !customEndDate) return null;
      return { startDate: customStartDate, endDate: customEndDate };
    }
  }, [periodMode, selectedYear, selectedMonth, customStartDate, customEndDate]);

  // データ取得
  const fetchStatements = useCallback(async () => {
    const range = getDateRange();
    if (!range) return;

    setLoading(true);
    setFetched(false);
    try {
      const params = new URLSearchParams({
        startDate: range.startDate,
        endDate: range.endDate,
      });

      const data = await api.get<StatementsResponse>(`/api/incentives/statements?${params.toString()}`);
      setStatements(data.statements);
      setGrandTotal(data.grandTotal);
      setFetched(true);

      // 全ユーザーを初期選択
      const ids = new Set(data.statements.map(s => s.userId));
      setSelectedUserIds(ids);
      setSelectAll(true);
    } catch (error) {
      console.error('インセンティブ明細取得エラー:', error);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  // 全選択/解除
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedUserIds(new Set(statements.map(s => s.userId)));
    } else {
      setSelectedUserIds(new Set());
    }
  };

  // 個別選択
  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // selectAllの同期
  useEffect(() => {
    if (statements.length > 0) {
      setSelectAll(selectedUserIds.size === statements.length);
    }
  }, [selectedUserIds, statements]);

  // PDFダウンロード（選択したユーザー分）
  const handleDownload = async () => {
    if (selectedUserIds.size === 0) {
      toast.error('ダウンロードするユーザーを選択してください');
      return;
    }

    setDownloading(true);
    try {
      const selectedStatements = statements.filter(s => selectedUserIds.has(s.userId));

      for (const stmt of selectedStatements) {
        const pdfData: IncentiveStatementData = {
          departmentName: stmt.departmentName,
          userName: stmt.userName,
          paymentDate,
          details: stmt.details,
          totalAmount: stmt.totalAmount,
          createdDate: getTodayStr(),
        };

        const { blob } = await generateIncentiveStatementPDF(pdfData);

        const periodLabel = periodMode === 'month'
          ? `${selectedYear}${String(selectedMonth).padStart(2, '0')}`
          : `${customStartDate}_${customEndDate}`;
        const fileName = `インセンティブ明細_${stmt.userName}_${periodLabel}.pdf`;
        downloadIncentiveStatementPDF(blob, fileName);

        // 連続ダウンロード時に少し間隔を空ける
        if (selectedStatements.length > 1) {
          await new Promise(r => setTimeout(r, 500));
        }
      }
    } catch (error) {
      console.error('PDFダウンロードエラー:', error);
      toast.error('PDFの生成に失敗しました');
    } finally {
      setDownloading(false);
    }
  };

  // ロール表示名
  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'assigned': return '担当';
      case 'pm': return 'PM';
      case 'promoter': return 'プロモーター';
      default: return role;
    }
  };

  // 金額フォーマット
  const formatYen = (amount: number) => `¥${amount.toLocaleString('ja-JP')}`;

  // 年の選択肢
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">インセンティブ明細</h1>
      </div>

      {/* 条件設定カード */}
      <Card>
        <div className="space-y-5">
          {/* 期間選択 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">期間</label>
            <div className="flex items-center gap-4 mb-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="periodMode"
                  value="month"
                  checked={periodMode === 'month'}
                  onChange={() => setPeriodMode('month')}
                  className="text-blue-600"
                />
                月指定
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="periodMode"
                  value="custom"
                  checked={periodMode === 'custom'}
                  onChange={() => setPeriodMode('custom')}
                  className="text-blue-600"
                />
                カスタム期間
              </label>
            </div>

            {periodMode === 'month' ? (
              <div className="flex items-center gap-2">
                <select
                  value={selectedYear}
                  onChange={e => setSelectedYear(Number(e.target.value))}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}年</option>
                  ))}
                </select>
                <select
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(Number(e.target.value))}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
                >
                  {months.map(m => (
                    <option key={m} value={m}>{m}月</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={e => setCustomStartDate(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
                />
                <span className="text-gray-500">〜</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={e => setCustomEndDate(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
                />
              </div>
            )}
          </div>

          {/* 支給日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">支給日</label>
            <input
              type="date"
              value={paymentDate}
              onChange={e => setPaymentDate(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* 検索ボタン */}
          <div>
            <Button onClick={fetchStatements} disabled={loading || (periodMode === 'custom' && (!customStartDate || !customEndDate))}>
              {loading ? '取得中...' : 'インセンティブを集計'}
            </Button>
          </div>
        </div>
      </Card>

      {/* ローディング */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loading size="lg" />
        </div>
      )}

      {/* 結果表示 */}
      {fetched && !loading && (
        <>
          {statements.length === 0 ? (
            <Card>
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                指定期間にインセンティブデータがありません
              </div>
            </Card>
          ) : (
            <>
              {/* 操作バー */}
              <Card>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={e => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300"
                      />
                      全員選択
                    </label>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedUserIds.size}/{statements.length}人 選択中
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      総合計: <span className="text-lg font-bold text-blue-600">{formatYen(grandTotal)}</span>
                    </span>
                    <Button onClick={handleDownload} disabled={downloading || selectedUserIds.size === 0}>
                      {downloading ? 'PDF生成中...' : `選択した${selectedUserIds.size}人分をダウンロード`}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* ユーザーごとの明細 */}
              <div className="space-y-4">
                {statements.map(stmt => (
                  <Card key={stmt.userId}>
                    <div className="space-y-3">
                      {/* ユーザーヘッダー */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(stmt.userId)}
                            onChange={() => handleToggleUser(stmt.userId)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300"
                          />
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded mr-2">
                              {stmt.departmentName}
                            </span>
                            <span className="text-base font-bold text-gray-900 dark:text-white">
                              {stmt.userName}
                            </span>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-blue-600">
                          {formatYen(stmt.totalAmount)}
                        </span>
                      </div>

                      {/* 明細テーブル */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                              <th className="text-left py-2 px-2 font-medium">案件番号</th>
                              <th className="text-left py-2 px-2 font-medium">顧客名</th>
                              <th className="text-left py-2 px-2 font-medium">工事内容</th>
                              <th className="text-center py-2 px-2 font-medium">種別</th>
                              <th className="text-right py-2 px-2 font-medium">金額</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stmt.details.map((d, idx) => (
                              <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                                <td className="py-2 px-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{d.constructionNumber}</td>
                                <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{d.companyName}</td>
                                <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{d.title}</td>
                                <td className="py-2 px-2 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    d.role === 'assigned' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                    d.role === 'pm' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                  }`}>
                                    {getRoleLabel(d.role)}
                                  </span>
                                </td>
                                <td className={`py-2 px-2 text-right font-medium whitespace-nowrap ${d.amount < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                                  {formatYen(d.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
